import { useState } from "react"

import { useClientsQuery } from "@/query/clientQuery"
import {
  useAddPromotionMutation,
  useDeletePromotionMutation,
  usePromotions,
  useUpdatePromotionMutation,
} from "@/query/promotionQuery"
import { useServicesQuery } from "@/query/serviceQuery"
import { useShopDataQuery } from "@/query/shopDataQuery"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  PromotionCreateModal,
  PromotionDetailsModal,
  PromotionListItem,
  Select,
} from "@okeys/shared-react-components"
import {
  PromotionData,
  PromotionTable,
  PromotionType,
} from "@okeys/shared-utils"
import { Plus, Search } from "lucide-react"

type PromotionFormData = {
  name: string
  description?: string
  type: PromotionType
  isActive: boolean
  startDate?: string
  endDate?: string
  eligibleServiceIds?: string[]
  eligibleClientIds?: string[]
  data: PromotionData[PromotionType]
}

export const Promotions = () => {
  // Utilisation des queries
  const {
    promotions,
    isLoadingPromotions,
    errorPromotions,
    refetchPromotions,
  } = usePromotions()
  const { shopData } = useShopDataQuery()
  const { services, pendingServices, errorServices } = useServicesQuery()
  const {
    clients,
    isLoading: isLoadingClients,
    isError: errorClients,
  } = useClientsQuery()

  const addPromotionMutation = useAddPromotionMutation()
  const updatePromotionMutation = useUpdatePromotionMutation()
  const deletePromotionMutation = useDeletePromotionMutation()
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all")
  const [typeFilter, setTypeFilter] = useState<"all" | PromotionType>("all")

  // Modal states
  const [modalOpen, setModalOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editingPromotion, setEditingPromotion] =
    useState<PromotionTable<PromotionType> | null>(null)

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [promotionToDelete, setPromotionToDelete] =
    useState<PromotionTable<PromotionType> | null>(null)

  // Plus besoin de useEffect et loadPromotions car les données sont gérées par React Query

  const handleAddPromotion = () => {
    setCreateModalOpen(true)
  }

  const handleEditPromotion = (promotion: PromotionTable<PromotionType>) => {
    setEditingPromotion(promotion)
    setModalOpen(true)
  }

  // Unused for now - kept for future delete functionality
  // const handleDeletePromotion = (promotion: PromotionTable<PromotionType>) => {
  //   setPromotionToDelete(promotion)
  //   setDeleteDialogOpen(true)
  // }

  const confirmDelete = async () => {
    if (!promotionToDelete) return

    try {
      await deletePromotionMutation.mutateAsync(promotionToDelete.id)
      setDeleteDialogOpen(false)
      setPromotionToDelete(null)
    } catch (error) {
      console.error("Erreur lors de la suppression:", error)
      // L'erreur est déjà gérée dans la mutation
    }
  }

  const handleSubmitPromotion = async (data: PromotionFormData) => {
    try {
      if (editingPromotion) {
        // Mode modification - utiliser la route update
        const updateData = {
          id: editingPromotion.id,
          name: data.name,
          description: data.description,
          type: data.type,
          isActive: data.isActive,
          startDate: data.startDate || undefined,
          endDate: data.endDate || undefined,
          eligibleClientIds: data.eligibleClientIds,
          data: data.data,
        }

        await updatePromotionMutation.mutateAsync(updateData)
        setModalOpen(false)
        setEditingPromotion(null)
      } else {
        // Mode création - utiliser la route add
        const promotionData = {
          name: data.name,
          description: data.description,
          type: data.type,
          isActive: data.isActive,
          startDate: data.startDate || undefined,
          endDate: data.endDate || undefined,
          eligibleServiceIds: data.eligibleServiceIds,
          eligibleClientIds: data.eligibleClientIds,
          data: data.data,
          lastUpdateDate: new Date().toISOString(),
          lastUpdatePersonType: "manager" as const,
          lastUpdatePersonId: "placeholder-id", // TODO: Remplacer par l'ID de l'utilisateur connecté
          lastUpdatePersonEmail: "placeholder-email", // TODO: Remplacer par l'email de l'utilisateur connecté
        }

        await addPromotionMutation.mutateAsync(promotionData)
        setModalOpen(false)
      }
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error)
      throw error
    }
  }

  // Filtrage des promotions
  const filteredPromotions = (promotions || []).filter((promotion) => {
    const matchesSearch =
      promotion.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      promotion.description?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && promotion.isActive) ||
      (statusFilter === "inactive" && !promotion.isActive)

    const matchesType = typeFilter === "all" || promotion.type === typeFilter

    return matchesSearch && matchesStatus && matchesType
  })

  if (isLoadingPromotions || pendingServices || isLoadingClients) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement des promotions...</p>
        </div>
      </div>
    )
  }

  if (errorPromotions || errorServices || errorClients) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            Erreur lors du chargement des promotions
          </p>
          <Button onClick={() => refetchPromotions()} variant="outline">
            Réessayer
          </Button>
        </div>
      </div>
    )
  }

  // Transformer les clients pour le format attendu par les composants
  const formattedClients = clients.map((client) => ({
    clientId: client.clientId,
    name: client.name,
    email: client.email || "",
  }))

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-row justify-between items-center gap-4">
        <Button onClick={handleAddPromotion}>
          <Plus className="h-4 w-4" />
          Nouvelle promotion
        </Button>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Rechercher une promotion..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div>
          <Select
            value={statusFilter}
            onChange={(value) =>
              setStatusFilter(value as "all" | "active" | "inactive")
            }
            options={[
              { value: "all", label: "Tous les statuts" },
              { value: "active", label: "Actives" },
              { value: "inactive", label: "Inactives" },
            ]}
            placeholder="Statut"
            className="w-[180px]"
          />
        </div>
        <div>
          <Select
            value={typeFilter}
            onChange={(value) => setTypeFilter(value as "all" | PromotionType)}
            options={[
              { value: "all", label: "Tous les types" },
              { value: "fixedPrice", label: "Prix fixe" },
              { value: "percentageDiscount", label: "Réduction %" },
              { value: "buyXGetYFree", label: "Achetez X, obtenez Y" },
            ]}
            placeholder="Type"
            className="w-[200px]"
          />
        </div>
      </div>

      {/* Promotions Grid */}
      {filteredPromotions.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-muted-foreground mb-4">
            {searchTerm || statusFilter !== "all" || typeFilter !== "all"
              ? "Aucune promotion ne correspond aux filtres"
              : "Aucune promotion trouvée"}
          </div>
          {!searchTerm && statusFilter === "all" && typeFilter === "all" && (
            <Button onClick={handleAddPromotion} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Créer votre première promotion
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-4">
          {filteredPromotions.map((promotion) => {
            // Adapter les données pour PromotionListItem
            const adaptedPromotion = {
              ...promotion,
              data: {
                [promotion.type]: promotion.data,
              } as Record<string, unknown>,
            }
            return (
              <PromotionListItem
                key={promotion.id}
                promotion={adaptedPromotion}
                onClick={() => handleEditPromotion(promotion)}
              />
            )
          })}
        </div>
      )}

      {/* Modal */}
      {editingPromotion && (
        <PromotionDetailsModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          promotion={{
            ...editingPromotion,
            type:
              editingPromotion.type === "buyXGetYDiscount"
                ? "buyXGetYDiscount"
                : editingPromotion.type,
            data: editingPromotion.data as PromotionData[typeof editingPromotion.type],
          }}
          clients={formattedClients}
          services={services || []}
          onSave={(promotion) => {
            const formData: PromotionFormData = {
              name: promotion.name,
              description: promotion.description,
              type: promotion.type,
              isActive: promotion.isActive,
              startDate: promotion.startDate,
              endDate: promotion.endDate,
              eligibleServiceIds: [],
              eligibleClientIds: promotion.eligibleClientIds,
              data: promotion.data as unknown as PromotionData[PromotionType],
            }
            handleSubmitPromotion(formData)
          }}
          onCancel={() => setModalOpen(false)}
        />
      )}

      {/* Modal de création de promotion */}
      <PromotionCreateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        shopId={shopData?.shopId || ""}
        services={services || []}
        clients={formattedClients}
        onSave={async (promotion) => {
          // Créer la nouvelle promotion
          const promotionData = {
            name: promotion.name || "",
            description: promotion.description,
            type: promotion.type as PromotionType,
            isActive: promotion.isActive || false,
            startDate: promotion.startDate || undefined,
            endDate: promotion.endDate || undefined,
            eligibleServiceIds: [],
            eligibleClientIds: promotion.eligibleClientIds,
            data: promotion.data as PromotionData[PromotionType],
            lastUpdateDate: new Date().toISOString(),
            lastUpdatePersonType: "manager" as const,
            lastUpdatePersonId: "placeholder-id", // TODO: Remplacer par l'ID de l'utilisateur connecté
            lastUpdatePersonEmail: "placeholder-email", // TODO: Remplacer par l'email de l'utilisateur connecté
          }
          await addPromotionMutation.mutateAsync(promotionData)
          setCreateModalOpen(false)
          refetchPromotions()
        }}
        onCancel={() => setCreateModalOpen(false)}
      />

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer la promotion "
              {promotionToDelete?.name}" ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deletePromotionMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletePromotionMutation.isPending
                ? "Suppression..."
                : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
